import {
  Paper,
  Group,
  Text,
  Stack,
  Slider,
  Box,
  Flex,
  ActionIcon,
  Combobox,
  useCombobox,
  Grid,
  Loader,
} from '@mantine/core';
import {
  IconPlayerPauseFilled,
  IconPlayerPlayFilled,
  IconRotateClockwise,
  IconRotate,
  IconClock,
} from '@tabler/icons-react';
import { FC, useContext, useEffect, useRef, useState } from 'react';
import ReportContext from 'context/ReportContext';

import classes from './RecordPlayer.module.scss';

const SPEEDS = ['0.5', '0.75', '1.0', '1.25', '1.5', '2.0'];
const SKIP_TIME = 10;
const CONTROL_COLOR = '#67676F';
const SPEED_OPTIONS = SPEEDS.map((speed) => ({
  value: speed,
  label: `${speed}x`,
}));

interface RecordPlayerProps {
  isUnavailable: boolean;
}

const RecordPlayer: FC<RecordPlayerProps> = ({ isUnavailable }) => {
  const {
    data: { callDetails, isLoadingCallDetails },
  } = useContext(ReportContext);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [speed, setSpeed] = useState(SPEED_OPTIONS[2].value);

  const audioUrl = callDetails.audio_recording_filepath;

  useEffect(() => {
    if (isUnavailable) {
      return;
    }

    const audio = new Audio(audioUrl);
    audioRef.current = audio;

    audio.addEventListener('loadedmetadata', () => {
      setDuration(audio.duration);
    });

    audio.addEventListener('timeupdate', () => {
      setCurrentTime(audio.currentTime);
    });

    audio.addEventListener('ended', () => {
      setIsPlaying(false);
    });

    return () => {
      audio.pause();
      audio.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioUrl]);

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const formatTimeRatio = () => {
    return `${formatTime(currentTime)} / ${formatTime(duration)}`;
  };

  const togglePlay = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (value: number) => {
    if (!audioRef.current) return;
    const time = (value / 100) * duration;
    audioRef.current.currentTime = time;
    setCurrentTime(time);
  };

  const handleSpeed = (newSpeed: string) => {
    if (!audioRef.current) return;
    audioRef.current.playbackRate = parseFloat(newSpeed);
    setSpeed(newSpeed);
  };

  const skipForward = () => {
    if (!audioRef.current) return;
    const newTime = Math.min(currentTime + SKIP_TIME, duration);
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const skipBackward = () => {
    if (!audioRef.current) return;
    const newTime = Math.max(currentTime - SKIP_TIME, 0);
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  return (
    <Paper withBorder p="md" radius="md" w="100%">
      <Stack gap="lg">
        <Grid align="center" justify="center" mx={{ xl: 'xl' }}>
          <Grid.Col span={{ base: 6, xs: 3 }} order={{ base: 2, xs: 1 }}>
            <Group justify="center" gap="xs">
              <Text size="sm" fw={600}>
                {formatTimeRatio()}
              </Text>
            </Group>
          </Grid.Col>

          <Grid.Col span={{ base: 12, xs: 6 }} order={{ base: 1, xs: 2 }}>
            <Flex
              align="center"
              justify="center"
              gap={{ base: 'md', md: 'lg' }}
            >
              <ActionIcon
                radius="xl"
                variant="default"
                size="lg"
                onClick={skipBackward}
                disabled={isLoadingCallDetails || isUnavailable}
                bg="var(--bg-color)"
              >
                <Text size="0.5rem" fw={600} style={{ position: 'absolute' }}>
                  {SKIP_TIME}
                </Text>
                <IconRotate
                  size={26}
                  stroke={1.2}
                  color={CONTROL_COLOR}
                  style={{ transform: 'rotate(180deg)' }}
                />
              </ActionIcon>
              <Box pos="relative">
                {isLoadingCallDetails && (
                  <Loader
                    size="46px"
                    style={{ left: '-1px', top: '-1px' }}
                    pos="absolute"
                    color="var(--primary-color)"
                    className={classes.playLoader}
                  />
                )}
                <ActionIcon
                  radius="xl"
                  variant="default"
                  size="xl"
                  bg="var(--bg-color)"
                  onClick={togglePlay}
                  disabled={isLoadingCallDetails || isUnavailable}
                >
                  {isPlaying ? (
                    <IconPlayerPauseFilled size={22} color={CONTROL_COLOR} />
                  ) : (
                    <IconPlayerPlayFilled size={22} color={CONTROL_COLOR} />
                  )}
                </ActionIcon>
              </Box>
              <ActionIcon
                radius="xl"
                variant="default"
                size="lg"
                onClick={skipForward}
                disabled={isLoadingCallDetails || isUnavailable}
                bg="var(--bg-color)"
              >
                <Text size="0.5rem" fw={600} style={{ position: 'absolute' }}>
                  {SKIP_TIME}
                </Text>
                <IconRotateClockwise
                  size={26}
                  stroke={1.2}
                  color={CONTROL_COLOR}
                  style={{ transform: 'rotate(180deg)' }}
                />
              </ActionIcon>
            </Flex>
          </Grid.Col>

          <Grid.Col span={{ base: 6, xs: 3 }} order={3}>
            <Group justify="center">
              <Combobox
                store={combobox}
                width="max-content"
                onOptionSubmit={(val) => {
                  handleSpeed(val);
                  combobox.updateSelectedOptionIndex('active');
                  combobox.closeDropdown();
                }}
              >
                <Combobox.Target>
                  <Flex
                    onClick={() => combobox.toggleDropdown()}
                    align="center"
                    gap={4}
                    style={{ cursor: 'pointer' }}
                  >
                    <IconClock size={22} stroke={1.5} color={CONTROL_COLOR} />
                    <Text size="sm" w="2.1875rem" fw={600}>
                      {speed}x
                    </Text>
                  </Flex>
                </Combobox.Target>
                {!isLoadingCallDetails && (
                  <Combobox.Dropdown>
                    <Combobox.Options>
                      {SPEED_OPTIONS.map((item) => {
                        const isActive = speed === item.value;
                        return (
                          <Combobox.Option
                            value={item.value}
                            key={item.value}
                            active={isActive}
                            style={{
                              backgroundColor: isActive
                                ? 'var(--mantine-color-gray-1)'
                                : 'transparent',
                            }}
                          >
                            {item.label}
                          </Combobox.Option>
                        );
                      })}
                    </Combobox.Options>
                  </Combobox.Dropdown>
                )}
              </Combobox>
            </Group>
          </Grid.Col>
        </Grid>

        <Box mt={{ base: 'xs', md: 0 }}>
          <Slider
            value={(currentTime / duration) * 100 || 0}
            onChange={handleSeek}
            label={null}
            disabled={isLoadingCallDetails || isUnavailable}
            size="sm"
            styles={{
              bar: {
                backgroundColor:
                  currentTime === 0 ? 'transparent' : 'var(--primary-color)',
              },
              thumb: {
                borderWidth: 0,
                width: 12,
                height: 6,
                backgroundColor:
                  currentTime === 0 ? 'transparent' : 'var(--primary-color)',
              },
            }}
          />
        </Box>
      </Stack>
    </Paper>
  );
};

export default RecordPlayer;
